<template>
    <div class="border border-dark border-1 rounded-lg">
        <div v-if="topBarVisible" class="mt-3">
            <button v-if="editVisible" class="btn btn-outline-secondary btn-sm me-2 d-none d-sm-inline" @click="showYearIncomeModal"
                :disabled="isReadOnly.editButtonsDisabled">Edit</button>
            <button v-if="setAllVisible" class="btn btn-outline-secondary btn-sm me-2 d-none d-sm-inline"
                @click="showSetupModal">{{ setAllText }}</button>
            <template v-if="rangeControlsVisible">
                <button class="btn btn-outline-secondary btn-sm me-2 d-none d-sm-inline" @click="increaseScale"
                    :disabled="isReadOnly.editButtonsDisabled">Increase range</button>
                <button class="btn btn-outline-secondary btn-sm me-2 d-none d-sm-inline" @click="resetTheScaleIfRequired"
                    :disabled="isReadOnly.editButtonsDisabled">Reset range</button>
            </template>
        </div>

        <div v-if="projectionControlsVisible" class="mt-3 d-flex flex-wrap">
            <div v-show="inflationPresentationChoiceVisible" class="dropdown me-3">
                <button ref="inflationDropDownBtn" type="button"
                    class="btn btn-outline-secondary rounded p-2 d-flex align-items-center dropdown-toggle bg-white"
                    :disabled="presentationChoiceButtonsDisabled" id="dropdownMenuButton" data-bs-toggle="dropdown"
                    aria-haspopup="true" aria-expanded="false">
                    <span class="me-2 form-check-label text-muted inflation-dropdown"
                        :class="{ 'disabled': presentationChoiceButtonsDisabled }">
                        {{ selectedInflationItem.abbrevText }}
                    </span>
                </button>
                <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                    <template v-for="(item, index) in inflationDropdownItems" :key="index">
                        <div class="dropdown-item p-1 px-2 d-flex align-items-center" tabindex="0"
                            @click.prevent="increasesDropdownItemClick($event, item)"
                            @keyup.enter="increasesDropdownItemEnter(item)">
                            <div style="display: inline-block;">
                                <span class="text-muted">{{ item.text }}</span><br>
                                <span class="text-muted fst-italic me-3" style="font-size: 0.8rem;">{{ item.subtext
                                }}</span>
                            </div>
                        </div>
                        <div v-if="index < inflationDropdownItems.length - 1" class="dropdown-divider"></div>
                    </template>
                </div>
            </div>

            <div id="showInTodaysMoneyButton" class="d-flex btn btn-outline-secondary p-2 align-items-center me-3 vspace-on-wrap"
                :class="{ 'disabled': presentationChoiceButtonsDisabled }">
                <div class="form-check form-switch me-2">
                    <input id="todaysMoneyCheckboxId" type="checkbox"
                        :disabled="presentationChoiceButtonsDisabled || presentationUpdateOngoing"
                        class="form-check-input todays-money-chk" v-model="showWhatThisIsWorthToday" role="switch" />
                    <label class="form-check-label text-muted todays-money-label" for="todaysMoneyCheckboxId">
                        Show in today's money
                    </label>
                </div>
                <help title="Today's money" :enabled="!presentationChoiceButtonsDisabled" :text="HelpText.todaysMoney()" />
            </div>
        </div>

        <IncomeDescriber :show-description="annualIncomes.length > 0" :initial-planning-year="initialPlanningYear"
            :plan-years="planYears" :retiring-on-jan-first="retiringOnJanFirst" :planYear="selectedYear"
            :amount="annualIncomes[selectedYear]" @year-selected="onMobileChartYearSelected" @edit="onMobileEditClicked"
            @decrement="onDecrementIncomeClicked" @increment="onIncrementIncomeClicked"
            :edit-disabled="isReadOnly.editButtonsDisabled" :increment-buttons-visible="false">
        </IncomeDescriber>

        <div class="mt-3 responsive-overflow d-none d-sm-block">
            <div class="d-flex justify-content-between slider-container">
                <IncomeScale :max-value="maxIncome" :disabled="!hasData"></IncomeScale>

                <AnnualExpense v-for="(annualIncome, i) in annualIncomes.slice(0, planYears)" :key="i"
                    v-model="annualIncomes[i]" :planYear="i + 1" :calendar-year="initialPlanningYear + i"
                    :starts-on-jan-first="retiringOnJanFirst" :max="maxIncome" :min="minIncome" :step="step"
                    :is-read-only="isReadOnly.slidersDisabled" :is-selected="selectedYear == i"
                    @got-focus="selectedYear = i" orientation="vertical">
                </AnnualExpense>

                <span v-if="annualIncomes.length == 0" class="flex-grow-1">&nbsp;</span>
            </div>
            <div class="d-flex flex-row justify-content-center mt-2">
                <p :class="getDisabledClasses(!hasData)">Retirement Year</p>
            </div>
        </div>

        <div class="d-sm-none">
            <MobileIncomeChart :data="mobileData" @year-selected="onMobileChartYearSelected"></MobileIncomeChart>
        </div>
    </div>

    <modal :title="setAllText" ref="incomeSetupModal" idKey="incomeSetup">
        <initial-income-setup ref="incomeSetupModalUI" :modalMode="true" :baseIncome="quickSetValuesTracker.baseIncome"
            :extraInitialIncome="quickSetValuesTracker.extraIncome" @populate-income-plan="setupIncome"
            @cancelled="closeSetupModal">
        </initial-income-setup>
    </modal>

    <modal :title="yearIncomeModalTitle" ref="yearIncomeModal" id-key="yearEditor">
        <YearEditor ref="yearIncomeModalUI" @update-year="updateYearIncome" @cancelled="closeYearIncomeModal"></YearEditor>
    </modal>

    <modal title="Income edit mode" ref="incomeEditModeModal" id-key="incomeEditMode" :close-button-aria-label="editModeConfirmationAria()">
        <EditableProjectionQuery ref="incomeEditModeModalUI" @edit-mode-required="onEditModeRequired" @cancelled="closeIncomeEditModeModal">
        </EditableProjectionQuery>
    </modal>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted, nextTick } from "vue";
import { useStore } from "vuex";
import { Retiree } from "@/guiide/ts/retiree";
import { createModernIncomeArray, tickCount, tickUnits, getDisabledClasses, calculateRequiredChartProjection, generateSteps, calculateBaseValueFromProjectedValue } from "@/guiide/ts/wants/wantsUtilities";
import { isJanuaryFirst, roundUp, roundUpToNearest } from "@/guiide/ts/utilities";
import { InitialIncomeProfile } from "@/guiide/ts/wants/initialIncomeProfile";
import { ChartView } from "@/guiide/ts/wants/chartView";
import IncomeDescriber from "./IncomeDescriber.vue";
import AnnualExpense from "./AnnualExpense.vue";
import IncomeScale from "./IncomeScale.vue";
import Modal from "@/guiide/components/common/Modal.vue";
import InitialIncomeSetup from './InitialIncomeSetup.vue';
import Help from "@/guiide/components/common/Help.vue";
import HelpText from "@/guiide/ts/helpText";
import ViewMode from "@/guiide/ts/wants/viewMode";
import Constants from "@/guiide/ts/constants";
import MobileIncomeChart from "./MobileIncomeChart.vue";
import YearEditor from "./YearEditor.vue";
import EditableProjectionQuery from "./EditableProjectionQuery.vue";
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import dateTimeProvider from "@/guiide/ts/systemDateTimeProvider";

type ReadState = { componentStateIsReadOnly: boolean, siteConfigStateIsRestricted: boolean, editButtonsDisabled: boolean, slidersDisabled: boolean };
type InflationDropdownInfo = { abbrevText: string, text: string, subtext: string, adjust: boolean };

const props = defineProps({
    isNewUser: { type: Boolean, default: false },
    inflationIncreaseEnabled: { type: Boolean, default: false },
    inflationAgeLimit: { type: Number, default: 200 }
});

const initialPlanningYear = ref(dateTimeProvider.now().getFullYear());
const retiringOnJanFirst = ref(false);
const minIncome = 7200;
const maxIncome = ref(30000);
const annualIncomes = reactive(new Array<number>());
const mobileData = reactive(new Array<number>());
const step = ref(Constants.IncomeStepSize);
let tempStorageOfAnnualIncomesReInf: Array<number> = [];
const selectedYear = ref(0);
const showInflationAdjusted = ref(true);
const showWhatThisIsWorthToday = ref(false);

const incomeSetupModal = ref(null);
const incomeSetupModalUI = ref(null);
const yearIncomeModal = ref(null);
const yearIncomeModalTitle = ref("Year n income");
const yearIncomeModalUI = ref(null);
const incomeEditModeModal = ref(null);
const incomeEditModeModalUI = ref(null);

const inflationDropDownBtn = ref(null);
const inflationPresentationChoiceVisible = ref(true);
const beforeIncreasesDropdownItem: InflationDropdownInfo = { abbrevText: 'Not including increases', text: 'Not including increases', subtext: 'Annual income without the increases added', adjust: false };
const afterIncreasesDropdownItem: InflationDropdownInfo = { abbrevText: 'Including increases ', text: 'Including increases', subtext: 'Annual income with wanted increases', adjust: true };
const inflationDropdownItems = [beforeIncreasesDropdownItem, afterIncreasesDropdownItem];
const selectedInflationItem = ref(inflationDropdownItems[1]);

const minScaleValue = 1000;
const maxScaleValue = 10000000;
const scaleHundredsThreshold = 400;

let planYears = 0;
let chartView: ChartView = { viewMode: ViewMode.AfterInflationNominal, inflationDurationYears: null };
const presentationUpdateOngoing = ref(false);
const quickSetValuesTracker = reactive({ baseIncome: 0, extraIncome: 0, extraIncomeYears: 5 } as InitialIncomeProfile);
const store = useStore();
const bootstrap: any = (window as any).bootstrap;

onMounted(() => {
    if (isReadOnly.value.siteConfigStateIsRestricted) {
        showInflationAdjusted.value = true;
        showWhatThisIsWorthToday.value = false;
        chartView = getRequiredChartView();
    }
});

watch(
    () => store.state.retiree,
    (newRetiree: Retiree) => {
        if (newRetiree == null) {
            return;
        }

        planYears = newRetiree.lifeExpectancy;

        const retirementDate = new Date(newRetiree.retirementDate);
        initialPlanningYear.value = retirementDate.getFullYear();
        retiringOnJanFirst.value = isJanuaryFirst(retirementDate);

        const userHasNotDefinedModernExpenses = newRetiree.requirements.income.length == 0;
        if (userHasNotDefinedModernExpenses) {
            return;
        }

        const incomeIncreasesWithInflation = newRetiree.requirements.incomeIncreasesWithInflation;
        const inflationDropdownItem = incomeIncreasesWithInflation
            ? afterIncreasesDropdownItem
            : beforeIncreasesDropdownItem;
        setSelectedDropdownItem(inflationDropdownItem);

        setIncome(newRetiree.requirements.income);
    }
);

const getIncome = function (): Array<number> {
    const incomeSource = incomeIsCurrentlyHeldInTempStorage() ? tempStorageOfAnnualIncomesReInf : annualIncomes;
    return Array.from({ length: Constants.MaxPlanYears }, (_, index) => incomeSource[index] || 0);
}

async function setIncome(newAnnualIncomes: Array<number>) {
    if (incomeIsCurrentlyHeldInTempStorage()) {
        tempStorageOfAnnualIncomesReInf = newAnnualIncomes;
        const force = true;
        const immediate = !hasData.value;
        updateChartPresentation(force, immediate);
    } else {
        const isInitialSetup = annualIncomes.length == 0;

        if (isInitialSetup) {
            const zeroIncomesForTransition = Array.from({ length: Constants.MaxPlanYears }, (_, index) => 0);
            annualIncomes.splice(0, annualIncomes.length, ...zeroIncomesForTransition);
        }

        await rebindChartData(newAnnualIncomes);
    }
}

defineExpose({ getIncome, setIncome });

const isInReadOnlyProjection = computed<boolean>(() => {
    return showInflationAdjusted.value || showWhatThisIsWorthToday.value;
});

const isReadOnly = computed<ReadState>(() => {
    const componentStateIsReadOnly = !hasData.value || isInReadOnlyProjection.value;
    const siteConfigStateIsRestricted = !store.state.configuration.requirementsExpenseEditPriviledges;
    const slidersDisabled = siteConfigStateIsRestricted ? true : componentStateIsReadOnly;
    // Edit buttons not disabled by read only component state as clicking on them will query the user as to whether they
    // want the component state to be changed to editable.
    const editButtonsDisabled = !hasData.value || siteConfigStateIsRestricted;

    const readState = { componentStateIsReadOnly, siteConfigStateIsRestricted, editButtonsDisabled, slidersDisabled };
    return readState;
});

const hasData = computed<boolean>(() => {
    return annualIncomes.length > 0 && annualIncomes.some(ai => ai != 0);
});

const topBarVisible = computed<boolean>(() => {
    if (props.isNewUser) {
        return !isReadOnly.value.siteConfigStateIsRestricted;
    }

    return true;
});

const editVisible = computed<boolean>(() => {
    return !isReadOnly.value.siteConfigStateIsRestricted;
});

const setAllVisible = computed<boolean>(() => {
    return !props.isNewUser;
});

const rangeControlsVisible = computed<boolean>(() => {
    return !isReadOnly.value.siteConfigStateIsRestricted;
});

const projectionControlsVisible = computed<boolean>(() => {
    return !isReadOnly.value.siteConfigStateIsRestricted;
});

const presentationChoiceButtonsDisabled = computed<boolean>(() => {
    if (!hasData.value) {
        return true;
    }

    if (showInflationAdjusted.value || showWhatThisIsWorthToday.value) {
        return false;
    }

    return isReadOnly.value.componentStateIsReadOnly;
});

const setAllText = computed<string>(() => {
    return isReadOnly.value.siteConfigStateIsRestricted ? "Edit" : "Set all";
});

const computedInflationIncreaseEnabled = computed<boolean>(() => props.inflationIncreaseEnabled);
watch(
    () => computedInflationIncreaseEnabled.value,
    (newValue: boolean) => {
        const inflationItem = newValue ? afterIncreasesDropdownItem : beforeIncreasesDropdownItem;
        setSelectedDropdownItem(inflationItem);
        inflationPresentationChoiceVisible.value = newValue;
    }
);

async function showSetupModal() {
    const suppressProjectionCheck = true;
    await initiateEditAction(async () => {
        incomeSetupModal.value.showModal(incomeSetupModalUI.value.initialize);
        return Promise.resolve();
    }, suppressProjectionCheck);
}

function closeSetupModal() {
    incomeSetupModal.value.closeModal();
}

function setupIncome(initialIncomeProfile: InitialIncomeProfile) {
    const retiree: Retiree = store.state.retiree;
    const totalPlanYears = retiree.lifeExpectancy;

    const incomeArray = createModernIncomeArray(
        initialIncomeProfile.baseIncome,
        initialIncomeProfile.extraIncome,
        initialIncomeProfile.extraIncomeYears,
        totalPlanYears,
        Constants.IncomeStepSize);

    setIncome(incomeArray);

    quickSetValuesTracker.baseIncome = initialIncomeProfile.baseIncome;
    quickSetValuesTracker.extraIncome = initialIncomeProfile.extraIncome;
    quickSetValuesTracker.extraIncomeYears = initialIncomeProfile.extraIncomeYears;

    closeSetupModal();
}

function onMobileEditClicked() {
    if (isReadOnly.value.siteConfigStateIsRestricted) {
        showSetupModal();
    } else {
        showYearIncomeModal();
    }
}

function showYearIncomeModal() {
    initiateEditAction(() => {
        yearIncomeModalTitle.value = `Year ${selectedYear.value + 1}`;
        yearIncomeModalUI.value.setIncome(annualIncomes[selectedYear.value]);
        yearIncomeModal.value.showModal(yearIncomeModalUI.value.initialize);
        return Promise.resolve();
    });
}

async function onDecrementIncomeClicked() {
    await initiateEditAction(async () => {
        const newIncome = annualIncomes[selectedYear.value] - Constants.IncomeStepSize;
        await updateIndividualIncomeValue(newIncome);
    });
}

async function onIncrementIncomeClicked() {
    await initiateEditAction(async () => {
        const newIncome = annualIncomes[selectedYear.value] + Constants.IncomeStepSize;
        await updateIndividualIncomeValue(newIncome);
    });
}

async function initiateEditAction(actor: () => Promise<void>, suppressProjectionCheck = false) {
    if (isInReadOnlyProjection.value && !suppressProjectionCheck) {
        incomeEditModeModal.value.showModal();
        return;
    }

    await actor();
}

function closeYearIncomeModal() {
    yearIncomeModal.value.closeModal();
}

function closeIncomeEditModeModal() {
    incomeEditModeModal.value.closeModal();
}

async function updateYearIncome(newIncome: number) {
    yearIncomeModal.value.closeModal();
    await updateIndividualIncomeValue(newIncome);
}

async function updateIndividualIncomeValue(newIncome: number) {
    newIncome = roundUp(newIncome, Constants.IncomeStepSize);
    const chartIsInBaseView = chartView.viewMode == ViewMode.BeforeInflationNominal;

    if (chartIsInBaseView) {
        const newIncomeArray = new Array<number>();
        annualIncomes.forEach((value: number, index: number) => { newIncomeArray.push(value) });
        newIncomeArray[selectedYear.value] = newIncome;
        await rebindChartData(newIncomeArray);
    } else {
        const baseAmountFromProjectedAmount = calculateBaseValueFromProjectedValue(chartView, newIncome, selectedYear.value, Constants.IncomeStepSize);
        tempStorageOfAnnualIncomesReInf[selectedYear.value] = baseAmountFromProjectedAmount;
        const force = true;
        await updateChartPresentation(force);
    }
}

async function increaseScale() {
    if (maxIncome.value > maxScaleValue) {
        return;
    }

    const increment = roundUpToNearest(maxIncome.value / tickCount, tickUnits);
    const newMax = maxIncome.value + increment;
    const fast = true;
    await updateTheScale(newMax, fast);
}

async function resetTheScaleIfRequired() {
    const scaleEvaluationResult = checkNewChartNumbersToSeeIfScaleNeedsToChange(annualIncomes);

    if (scaleEvaluationResult.needsToChange) {
        await updateTheScale(scaleEvaluationResult.newMax);
    }
}

function roundAtHundredPoint(num: number): number {
    const hundredsValue = num % 1000;

    if (hundredsValue < scaleHundredsThreshold) {
        return Math.floor(num / 1000) * 1000;
    } else {
        return Math.ceil(num / 1000) * 1000;
    }
}

watch([showInflationAdjusted, showWhatThisIsWorthToday], async () => await updateChartPresentation());

const computedInflationAgeLimit = computed<number>(() => props.inflationAgeLimit);
watch(computedInflationAgeLimit, async () => await updateChartPresentation());

async function updateChartPresentation(force = false, immediate = false) {
    presentationUpdateOngoing.value = true;

    if (!hasData.value && tempStorageOfAnnualIncomesReInf.length == 0) {
        presentationUpdateOngoing.value = false;
        return;
    }

    const newChartView = getRequiredChartView();

    if (!force && !hasChartViewChanged(newChartView)) {
        presentationUpdateOngoing.value = false;
        return;
    }

    let newChartNumbers: Array<number>;

    if (newChartView.viewMode == ViewMode.BeforeInflationNominal) {
        newChartNumbers = tempStorageOfAnnualIncomesReInf;
        tempStorageOfAnnualIncomesReInf = [];
    } else {
        ensureBeforeInflationNominalNumbersInTempStorage();
        newChartNumbers = calculateRequiredChartProjection(newChartView, tempStorageOfAnnualIncomesReInf, Constants.IncomeStepSize);
    }

    await rebindChartData(newChartNumbers, immediate);

    chartView = newChartView;
    presentationUpdateOngoing.value = false;
}

function incomeIsCurrentlyHeldInTempStorage(): boolean {
    return chartView.viewMode != ViewMode.BeforeInflationNominal;
}

function hasChartViewChanged(newChartView: ChartView): boolean {
    if (chartView.viewMode != newChartView.viewMode) {
        return true;
    }

    if (chartView.inflationDurationYears != newChartView.inflationDurationYears) {
        return true;
    }

    return false;
}

function getRequiredChartView(): ChartView {
    let viewMode: ViewMode;
    let inflationDurationYears: null | number = null;

    if (showInflationAdjusted.value) {
        viewMode = showWhatThisIsWorthToday.value ? ViewMode.AfterInflationReal : ViewMode.AfterInflationNominal;
        inflationDurationYears = computedInflationAgeLimit.value;
    } else {
        viewMode = showWhatThisIsWorthToday.value ? ViewMode.BeforeInflationReal : ViewMode.BeforeInflationNominal;
    }

    return { viewMode, inflationDurationYears };
}

function checkNewChartNumbersToSeeIfScaleNeedsToChange(newChartNumbers: number[]): { needsToChange: boolean, newMax: number } {

    const currentMax = Math.max(...newChartNumbers);

    let recalibrationRequired = false;

    const adjustor = 1.22;
    const preciseScaleMax = currentMax * adjustor;
    let idealScaleMax = roundAtHundredPoint(preciseScaleMax);

    if (preciseScaleMax < minScaleValue && currentMax < scaleHundredsThreshold) {
        idealScaleMax = minScaleValue;
        recalibrationRequired = true;
    } else if (preciseScaleMax < minScaleValue) {
        idealScaleMax = minScaleValue + 1000;
        recalibrationRequired = true;
    }

    if (currentMax >= maxIncome.value) {
        recalibrationRequired = true;
    } else {
        const driftFromIdeal = Math.abs(idealScaleMax - maxIncome.value);
        const allowedDriftFactor = 0.05;
        const allowedDrift = allowedDriftFactor * idealScaleMax;
        recalibrationRequired = recalibrationRequired || driftFromIdeal > allowedDrift;
    }

    return recalibrationRequired
        ? { needsToChange: true, newMax: idealScaleMax }
        : { needsToChange: false, newMax: 0 };
}

async function updateTheScale(newMax: number, fast = false) {
    const steps = 50;
    const scaleTransSteps = generateSteps(maxIncome.value, newMax, steps);
    const timeout = 7;
    await processArrayWithTimeout<number>(scaleTransSteps, 0, (index, maxValue) => maxIncome.value = maxValue, timeout);
}

async function processArrayWithTimeout<TArrayContent>(array: Array<TArrayContent>, index = 0, actor: (index: number, array: TArrayContent) => void, timeout: number): Promise<void> {
    if (index >= array.length) {
        return;
    }

    actor(index, array[index]);

    await new Promise(resolve => setTimeout(resolve, timeout));
    await processArrayWithTimeout(array, index + 1, actor, timeout);
}

async function rebindChartData(newData: Array<number>, immediate = false) {
    const scaleEvaluationResult = checkNewChartNumbersToSeeIfScaleNeedsToChange(newData);
    const changeScaleFirst = scaleEvaluationResult.newMax > maxIncome.value;

    const checkScale = async function () {
        if (scaleEvaluationResult.needsToChange) {
            await updateTheScale(scaleEvaluationResult.newMax);
        }
    };

    const pauseBetweenMovements = 200;

    if (changeScaleFirst) {
        await checkScale();
        await new Promise(resolve => setTimeout(resolve, pauseBetweenMovements));
    }

    await transitionSliders(newData, immediate);

    if (!changeScaleFirst) {
        await new Promise(resolve => setTimeout(resolve, pauseBetweenMovements));
        await checkScale();
    }
}

async function transitionSliders(newData: Array<number>, immediate = false) {
    newData.slice(0, planYears).forEach((value: number, index: number) => { mobileData[index] = value });

    if (immediate) {
        newData.forEach((value: number, index: number) => { annualIncomes[index] = value; });
        return;
    }

    step.value = 1;
    await nextTick();

    const steps = 61; //113; //73; //83;
    const timeout = 1; //5;
    const transitions = new Array<Array<number>>(newData.length);

    for (let i = 0; i < newData.length; i++) {
        transitions[i] = generateSteps(annualIncomes[i], newData[i], steps, (v) => Math.round(v));
    }

    const projections = new Array<Array<number>>(steps);
    for (let step = 0; step < steps; step++) {
        projections[step] = transitions.map(transition => transition[step]);
    }

    const projectionActor = function (index: number, array: Array<number>) {
        array.forEach((value: number, index: number) => {
            annualIncomes[index] = value;
        });
    }

    await processArrayWithTimeout(projections, 0, projectionActor, timeout);

    step.value = Constants.IncomeStepSize;
    await nextTick();
}

function ensureBeforeInflationNominalNumbersInTempStorage() {
    const alreadyCaptured = tempStorageOfAnnualIncomesReInf.length > 0;

    if (alreadyCaptured) {
        return;
    }

    annualIncomes.forEach((value: number) => {
        tempStorageOfAnnualIncomesReInf.push(value);
    });
}

function increasesDropdownItemEnter(item) {
    bootstrap.Dropdown.getInstance(inflationDropDownBtn.value).hide();
    increasesDropdownItemClick(null, item);
}

function increasesDropdownItemClick(event, item) {
    if (event) {
        event.preventDefault();
    }

    setTimeout(function () {
        setSelectedDropdownItem(item);
        inflationDropDownBtn.value.focus();
    }, 400);
}

function setSelectedDropdownItem(item: InflationDropdownInfo) {
    selectedInflationItem.value = item;
    showInflationAdjusted.value = item.adjust;
}

function onMobileChartYearSelected(year: number) {
    selectedYear.value = year;
}

async function onEditModeRequired() {
    incomeEditModeModal.value.closeModal();
    setSelectedDropdownItem(beforeIncreasesDropdownItem);
    showWhatThisIsWorthToday.value = false;
}

function editModeConfirmationAria(): string {
    return "Close button. To make it clearer, this chart is only editable when viewed with no increases added and not being shown in today's money. "
        + "Do you want to switch to this view so you can edit any particular year? Tab to yes button, enter to close.";
}

</script>

<style scoped>
.spacer {
    min-width: 70px;
    position: sticky;
    left: 0;
    opacity: 1;
    background-color: white;
    z-index: 1;
}

.disabled {
    opacity: 0.65;
}

.inflation-dropdown {
    width: 192px;
    text-align: left;
}

.slider-container {
    height: 300px;
}

.responsive-overflow {
    overflow: unset;
}

@media (max-width: 992px) {
    .responsive-overflow {
        overflow: auto;
    }

    .slider-container {
        min-width: 1024px;
    }
}

.disabled {
    opacity: 0.65;
    cursor: default;
}

.dropdown-toggle {
    height: 100%;
}

.dropdown-item:hover {
    cursor: pointer;
}

#dropdownMenuButton::after {
    color: black;
}

#dropdownMenuButton[disabled]::after {
    color: silver;
}

#showInTodaysMoneyButton:hover {
    background-color: white !important;
    cursor: pointer;
}

.todays-money-chk:hover {
    cursor: pointer;
}

.todays-money-label:hover {
    cursor: pointer;
}

@media (max-width: 618px) {
  .vspace-on-wrap {
    margin-top: 1rem !important;
  }
}
</style>